import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Phone, MapPin, Mail, Clock, Instagram, Facebook, Twitter, Linkedin, Youtube } from "lucide-react";
import { tplApi } from "@/lib/templateApi";

const API = process.env.REACT_APP_BACKEND_URL;
const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, twitter: Twitter, linkedin: Linkedin, youtube: Youtube };

export default function PublicSite() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    tplApi.getPublicContent(slug).then(setData).catch((e) => setError(e.message));
  }, [slug]);

  if (error) return <div className="min-h-screen bg-gray-900 flex items-center justify-center"><div className="text-center px-6"><h1 className="text-2xl text-white font-bold mb-2">Site not found</h1><p className="text-gray-400">{error}</p></div></div>;
  if (!data) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="w-6 h-6 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" /></div>;

  const { tenant, content } = data;
  const th = tenant.theme || {};
  const primary = th.primary_color || "#1E3A5F";
  const secondary = th.secondary_color || "#F59E0B";
  const font = th.font || "Inter";
  const hero = content.hero || {};
  const about = content.about;
  const contact = content.contact;
  const hours = content.hours;
  const footer = content.footer;
  const tz = tenant.timezone || "America/Phoenix";

  const navLinks = [];
  if (about) navLinks.push({ label: "About", href: "#about" });
  if (hours) navLinks.push({ label: "Hours", href: "#hours" });
  if (contact) navLinks.push({ label: "Contact", href: "#contact" });

  return (
    <div data-testid="public-site" style={{ fontFamily: `'${font}', sans-serif` }}>
      {/* ─── Nav ─── */}
      <nav className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <span className="text-base sm:text-lg font-bold truncate mr-3" style={{ color: primary }}>{tenant.name}</span>
          <div className="hidden sm:flex items-center gap-6">
            {navLinks.map((l) => <a key={l.href} href={l.href} className="text-sm text-gray-600 hover:text-gray-900 transition-colors">{l.label}</a>)}
          </div>
          {hero.cta_text && <a href={hero.cta_link || "#"} className="text-xs sm:text-sm font-semibold text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shrink-0" style={{ background: primary }}>{hero.cta_text}</a>}
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section data-testid="public-hero" className="relative flex items-center justify-center text-center px-4 sm:px-6 pt-14 sm:pt-16" style={{ minHeight: "100vh" }}>
        {hero.background_image && <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${hero.background_image})` }} />}
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(0,0,0,0.45) 0%, ${primary}dd 100%)` }} />
        <div className="relative z-10 max-w-3xl w-full">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-3 sm:mb-4" data-testid="hero-headline">{hero.headline || tenant.name}</h1>
          {hero.subheadline && <p className="text-white/80 text-sm sm:text-base md:text-lg mb-6 sm:mb-8 max-w-xl mx-auto" data-testid="hero-subheadline">{hero.subheadline}</p>}
          {hero.cta_text && <a href={hero.cta_link || "#"} className="inline-block px-6 sm:px-8 py-2.5 sm:py-3 rounded-full text-white text-sm sm:text-base font-semibold shadow-lg hover:scale-105 transition-transform" style={{ background: secondary }} data-testid="hero-cta">{hero.cta_text}</a>}
        </div>
      </section>

      {/* ─── About ─── */}
      {about && (
        <section id="about" data-testid="public-about" className="py-14 sm:py-20 px-4 sm:px-6 bg-slate-50">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
            {about.photo_url && <div className="h-56 sm:h-72 md:h-96 rounded-xl bg-cover bg-center shadow-sm" style={{ backgroundImage: `url(${about.photo_url})` }} />}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{about.headline || "About Us"}</h2>
              <div className="w-12 h-1 rounded-full mb-4 sm:mb-6" style={{ background: primary }} />
              {(about.body || "").split("\n\n").map((p, i) => p.trim() && <p key={i} className="text-gray-600 text-sm sm:text-base leading-relaxed mb-3">{p}</p>)}
            </div>
          </div>
        </section>
      )}

      {/* ─── Hours + Contact ─── */}
      {(hours || contact) && (
        <section id="contact" data-testid="public-contact-hours" className="py-14 sm:py-20 px-4 sm:px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 text-center">Visit Us</h2>
            <div className="w-12 h-1 rounded-full mb-8 sm:mb-12 mx-auto" style={{ background: primary }} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {hours && (
                <div id="hours" data-testid="public-hours" className="bg-slate-50 rounded-xl p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock size={18} style={{ color: primary }} />
                    <h3 className="font-bold text-gray-900">Hours</h3>
                    <OpenNowBadge hours={hours} tz={tz} />
                  </div>
                  <div className="space-y-2">
                    {(hours.days || []).map((d) => (
                      <div key={d.day} className="flex justify-between text-sm">
                        <span className="text-gray-700 font-medium">{d.day}</span>
                        <span className="text-gray-500">{d.closed ? "Closed" : `${d.open} – ${d.close}`}</span>
                      </div>
                    ))}
                  </div>
                  {hours.special_notes && <p className="text-xs text-gray-400 mt-4 italic">{hours.special_notes}</p>}
                </div>
              )}
              {contact && (
                <div data-testid="public-contact-info">
                  <h3 className="font-bold text-gray-900 mb-4">Contact</h3>
                  <div className="space-y-3">
                    {contact.address && <div className="flex items-start gap-2 text-sm text-gray-600"><MapPin size={15} style={{ color: primary }} className="mt-0.5 shrink-0" /><span>{contact.address}</span></div>}
                    {contact.phone && <div className="flex items-center gap-2 text-sm text-gray-600"><Phone size={15} style={{ color: primary }} /><a href={`tel:${contact.phone}`} className="hover:text-gray-900">{contact.phone}</a></div>}
                    {contact.email && <div className="flex items-center gap-2 text-sm text-gray-600"><Mail size={15} style={{ color: primary }} /><a href={`mailto:${contact.email}`} className="hover:text-gray-900">{contact.email}</a></div>}
                  </div>
                  {contact.google_map_embed && <div className="mt-4 rounded-lg overflow-hidden h-36 sm:h-40 bg-gray-100"><iframe src={contact.google_map_embed} width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" title="Map" /></div>}
                </div>
              )}
              {contact?.form_enabled !== false && <ContactForm slug={slug} primary={primary} />}
            </div>
          </div>
        </section>
      )}

      {/* ─── Footer ─── */}
      <footer data-testid="public-footer" className="py-8 sm:py-10 px-4 sm:px-6" style={{ background: primary }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <span className="font-bold text-white text-sm sm:text-base">{tenant.name}</span>
            {footer?.links?.length > 0 && (
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                {footer.links.map((l, i) => l.label && <a key={i} href={l.url} className="text-white/50 text-xs hover:text-white/70">{l.label}</a>)}
              </div>
            )}
          </div>
          {footer?.social && (() => {
            const active = Object.entries(footer.social).filter(([, v]) => v);
            return active.length > 0 ? (
              <div className="flex gap-4 justify-center mt-4">
                {active.map(([k, v]) => {
                  const Icon = SOCIAL_ICONS[k];
                  return Icon ? <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white/70 transition-colors"><Icon size={18} /></a>
                    : <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white/70 text-xs capitalize">{k}</a>;
                })}
              </div>
            ) : null;
          })()}
          <p className="text-white/30 text-xs text-center mt-4">&copy; {footer?.copyright || `${new Date().getFullYear()} ${tenant.name}. All rights reserved.`}</p>
          <p className="text-white/20 text-[10px] text-center mt-1">Powered by <a href="/" className="underline hover:text-white/30">SiteSmith</a></p>
        </div>
      </footer>
    </div>
  );
}

/* ─── Open Now badge with 60s auto-refresh ─── */
function OpenNowBadge({ hours, tz }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  try {
    const now = new Date();
    const dayName = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "long" }).format(now);
    const day = (hours.days || []).find((d) => d.day === dayName);
    if (!day || day.closed) return <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold">Closed</span>;

    const localTime = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", hour12: false }).format(now);
    const [h, m] = localTime.split(":").map(Number);
    const nowMins = h * 60 + m;
    const parseToMins = (t) => {
      const [time, ampm] = t.split(" ");
      let [hr, min] = time.split(":").map(Number);
      if (ampm === "PM" && hr !== 12) hr += 12;
      if (ampm === "AM" && hr === 12) hr = 0;
      return hr * 60 + (min || 0);
    };
    const openMins = parseToMins(day.open);
    const closeMins = parseToMins(day.close);
    const isOpen = nowMins >= openMins && nowMins < closeMins;
    // suppress unused tick warning
    void tick;
    return isOpen
      ? <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">Open Now</span>
      : <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold">Closed</span>;
  } catch { return null; }
}

/* ─── Contact form ─── */
function ContactForm({ slug, primary }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await fetch(`${API}/api/template/${slug}/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      setSent(true);
    } catch {} finally { setLoading(false); }
  };

  if (sent) return <div className="bg-emerald-50 rounded-xl p-5 sm:p-6 flex items-center justify-center text-emerald-700 text-sm font-medium" data-testid="contact-form-success">Message sent! We'll be in touch.</div>;

  return (
    <form onSubmit={submit} className="bg-slate-50 rounded-xl p-5 sm:p-6" data-testid="public-contact-form">
      <h3 className="font-bold text-gray-900 mb-4">Send a Message</h3>
      <div className="space-y-3">
        <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Your Name" required
          className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300" data-testid="contact-form-name" />
        <input value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} type="email" placeholder="Email" required
          className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300" data-testid="contact-form-email" />
        <textarea value={form.message} onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))} placeholder="How can we help?" rows={3} required
          className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white placeholder-gray-400 resize-none focus:outline-none focus:ring-1 focus:ring-gray-300" data-testid="contact-form-message" />
        <button type="submit" disabled={loading} className="w-full py-2.5 rounded-lg text-white font-semibold text-sm transition-colors disabled:opacity-50" style={{ background: primary }} data-testid="contact-form-submit">
          {loading ? "Sending..." : "Send Message"}
        </button>
      </div>
    </form>
  );
}
